import { useCallback, useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import { SOCKET_URL } from "../../../socket";

// The RTMS bridge (recall_test/rtms-quickstart-py) started life as a
// separate standalone service with its own URL, but has since folded into
// the main Arta backend (server/app/sockets_zoom.py) -- same server, same
// port. This hook still opens its own distinct socket.io-client connection
// (a second `sid` alongside useTseSocket's) rather than reusing that one
// directly; that's a deliberate smaller-diff choice, not a constraint --
// collapsing the two into one connection is a reasonable future cleanup.
const BRIDGE_URL = SOCKET_URL;

/**
 * useZoomBridgeSocket — owns the direct browser<->RTMS-bridge connection.
 * Only connects when `enabled` is true, so components that don't use the
 * Zoom feature (Dashboard5's copy of CustomerChatPanel) never open this
 * socket at all.
 *
 * handlers: { onTranscript, onBound, onError, onPaused, onResumed, onStopped,
 *             onCallFacts }
 */
export default function useZoomBridgeSocket({
  enabled = false, onTranscript, onBound, onError, onPaused, onResumed, onStopped, onCallFacts,
} = {}) {
  const socketRef = useRef(null);
  const handlersRef = useRef({});
  handlersRef.current = { onTranscript, onBound, onError, onPaused, onResumed, onStopped, onCallFacts };
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;

    const sock = io(BRIDGE_URL, { transports: ["websocket", "polling"] });
    socketRef.current = sock;

    const onConnect = () => setIsConnected(true);
    const onDisconnect = () => setIsConnected(false);
    const onTranscriptEvt = (payload) => handlersRef.current.onTranscript?.(payload);
    const onBoundEvt = (payload) => handlersRef.current.onBound?.(payload);
    const onErrorEvt = (payload) => handlersRef.current.onError?.(payload);
    const onPausedEvt = (payload) => handlersRef.current.onPaused?.(payload);
    const onResumedEvt = (payload) => handlersRef.current.onResumed?.(payload);
    const onStoppedEvt = (payload) => handlersRef.current.onStopped?.(payload);
    // Server pushes the synthesized call notes as they're rebuilt in the
    // background, so the send button never has to ask for them.
    const onCallFactsEvt = (payload) => handlersRef.current.onCallFacts?.(payload);

    sock.on("connect", onConnect);
    sock.on("disconnect", onDisconnect);
    sock.on("zoom:transcript", onTranscriptEvt);
    sock.on("zoom:bound", onBoundEvt);
    sock.on("zoom:error", onErrorEvt);
    sock.on("zoom:paused", onPausedEvt);
    sock.on("zoom:resumed", onResumedEvt);
    sock.on("zoom:stopped", onStoppedEvt);
    sock.on("zoom:call_facts", onCallFactsEvt);

    return () => {
      sock.off("connect", onConnect);
      sock.off("disconnect", onDisconnect);
      sock.off("zoom:transcript", onTranscriptEvt);
      sock.off("zoom:bound", onBoundEvt);
      sock.off("zoom:error", onErrorEvt);
      sock.off("zoom:paused", onPausedEvt);
      sock.off("zoom:resumed", onResumedEvt);
      sock.off("zoom:stopped", onStoppedEvt);
      sock.off("zoom:call_facts", onCallFactsEvt);
      sock.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    };
  }, [enabled]);

  const bindMeeting = useCallback((sessionId, meetingRef, agentName) => {
    socketRef.current?.emit("zoom:bind", { session_id: sessionId, meeting_ref: meetingRef, agent_name: agentName });
  }, []);

  // Ask the server to synthesize the call now. The answer arrives as a
  // zoom:call_facts push (onCallFacts), not as a return value.
  const syncFacts = useCallback((sessionId) => {
    socketRef.current?.emit("zoom:sync_facts", { session_id: sessionId });
  }, []);

  const pauseTranscript = useCallback((sessionId) => {
    socketRef.current?.emit("zoom:pause", { session_id: sessionId });
  }, []);

  const resumeTranscript = useCallback((sessionId) => {
    socketRef.current?.emit("zoom:resume", { session_id: sessionId });
  }, []);

  const stopTranscript = useCallback((sessionId) => {
    socketRef.current?.emit("zoom:stop", { session_id: sessionId });
  }, []);

  return { isConnected, bindMeeting, syncFacts, pauseTranscript, resumeTranscript, stopTranscript };
}
